import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { getRouteForRole, getPostLoginRedirect } from '../../lib/roleRoutes';
import Icon from '../../components/AppIcon';

const AuthCallback = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('');

  // ── Process Google OAuth user ─────────────────────────────────
  const processUser = async (user) => {
    try {
      const userEmail = user?.email?.toLowerCase();
      const userId    = user?.id;

      // Check user_profiles by user_id (primary key)
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('user_id, email, full_name, role, is_active')
        .eq('user_id', userId)
        .maybeSingle();

      // Profile exists — check completeness (mobile + GSTIN/PAN required)
      if (profile) {
        if (!profile?.is_active) {
          await supabase?.auth?.signOut();
          setStatus('error');
          setMessage('Your account has been deactivated. Please contact support.');
          return;
        }

        // If role is already set — fully registered user (WhatsApp / admin / prior OAuth)
        // Skip completion gate, go straight to dashboard
        if (profile?.role) {
          localStorage.setItem('userRole',  profile?.role);
          localStorage.setItem('userEmail', profile?.email);
          localStorage.setItem('userName',  profile?.full_name || '');
          localStorage.setItem('userId',    profile?.user_id);
          navigate(getPostLoginRedirect() || getRouteForRole(profile?.role), { replace: true });
          return;
        }

        // No role yet — new Google OAuth user, needs registration completion.
        // Carry ?next along so user-registration can still return the user to
        // their intended destination (e.g. a Mandi-selected Marketplace offer)
        // once they finish completing their profile.
        {
          const next = getPostLoginRedirect();
          const nextParam = next ? `&next=${encodeURIComponent(next)}` : '';
          navigate(`/user-registration?mode=complete&email=${encodeURIComponent(userEmail)}&uid=${userId}${nextParam}`, { replace: true });
        }
        return;
      }

      // Profile does not exist — redirect to registration completion
      {
        const next = getPostLoginRedirect();
        const nextParam = next ? `&next=${encodeURIComponent(next)}` : '';
        navigate(`/user-registration?mode=complete&email=${encodeURIComponent(userEmail)}&uid=${userId}${nextParam}`, { replace: true });
      }

    } catch (err) {
      console.error('Auth callback processUser error:', err);
      setStatus('error');
      setMessage('Something went wrong. Please try logging in again.');
    }
  };

  // ── Handle OAuth callback ─────────────────────────────────────
  useEffect(() => {
    let handled = false;

    const handleCallback = async () => {
      try {
        // Try 1: PKCE code in URL query params
        const urlParams = new URLSearchParams(window.location?.search);
        const code = urlParams?.get('code');

        if (code) {
          const { data, error } = await supabase?.auth?.exchangeCodeForSession(code);
          if (error) {
            console.error('Code exchange error:', error);
            setStatus('error');
            setMessage('Authentication failed. Please try again.');
            return;
          }
          if (data?.session?.user) {
            handled = true;
            await processUser(data?.session?.user);
            return;
          }
        }

        // Try 2: Existing session (hash-based flow)
        const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
        if (!sessionError && session?.user) {
          handled = true;
          await processUser(session?.user);
          return;
        }

        // Try 3: Auth state change listener
        const { data: { subscription } } = supabase?.auth?.onAuthStateChange(async (event, session) => {
          if (handled) return;
          if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && session?.user) {
            handled = true;
            subscription?.unsubscribe();
            await processUser(session?.user);
          }
        });

        // Timeout after 10 seconds
        setTimeout(() => {
          if (!handled) {
            subscription?.unsubscribe();
            setStatus('error');
            setMessage('Authentication timed out. Please try logging in again.');
          }
        }, 10000);

      } catch (err) {
        console.error('Auth callback error:', err);
        setStatus('error');
        setMessage('Something went wrong. Please try logging in again.');
      }
    };

    handleCallback();
  }, []);

  // ── Error / not found state ───────────────────────────────────
  if (status === 'error' || status === 'new_user') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#f8f1e9' }}>
        <div className="bg-white rounded-2xl shadow-lg border border-amber-100 p-10 max-w-md w-full text-center">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
            style={{ backgroundColor: status === 'error' ? '#fef2f2' : '#fef3c7' }}
          >
            <Icon
              name={status === 'error' ? 'AlertCircle' : 'UserX'}
              size={36}
              color={status === 'error' ? '#dc2626' : '#f59e0b'}
            />
          </div>
          <h2 className="text-xl font-bold mb-2" style={{ color: '#1e4a72' }}>
            {status === 'error' ? 'Login Failed' : 'Account Not Found'}
          </h2>
          <p className="text-sm mb-6" style={{ color: '#6b7280' }}>
            {message}
          </p>
          <button
            onClick={() => navigate('/login-authentication', { replace: true })}
            className="w-full py-3 rounded-xl font-semibold text-sm text-white transition hover:opacity-90"
            style={{ backgroundColor: '#1e4a72' }}
          >
            Back to Login
          </button>
          <button
            onClick={() => navigate('/register', { replace: true })}
            className="w-full py-3 rounded-xl font-semibold text-sm mt-3 transition hover:opacity-90"
            style={{ backgroundColor: '#f59e0b', color: '#fff' }}
          >
            Register Now
          </button>
        </div>
      </div>
    );
  }

  // ── Processing state ──────────────────────────────────────────
  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#f8f1e9' }}>
      <div className="bg-white rounded-2xl shadow-lg border border-amber-100 p-10 max-w-md w-full text-center">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: '#eff6ff' }}>
          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        </div>
        <h2 className="text-xl font-bold mb-2" style={{ color: '#1e4a72' }}>Signing you in...</h2>
        <p className="text-sm" style={{ color: '#6b7280' }}>Please wait while we verify your account.</p>
      </div>
    </div>
  );
};

export default AuthCallback;


